import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { previewFolio, listFraps, deleteFrap, getFrap, updateFrapServicio } from '@/utils/api';
import { FilePlus, LogOut, User, Trash2, Pencil, FileDown, AlertCircle, Shield, Ambulance, Eye } from 'lucide-react';
import { generateFrapPdf } from '@/utils/generatePdf';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9c5cf9e4-7ffa-44c2-89bf-7aacf8535caa/artifacts/4q3hto4h_634037357_1389521753189000_3144761508300406294_n.jpg';

const PRIORITY_STYLE = {
  ROJO: 'bg-red-100 text-red-700 border-red-300',
  VERDE: 'bg-green-100 text-green-700 border-green-300',
  AMARILLO: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  NEGRO: 'bg-gray-800 text-white border-gray-900',
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [nextFolio, setNextFolio] = useState('...');
  const [fraps, setFraps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [searchFolio, setSearchFolio] = useState('');
  const [searchUser, setSearchUser] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [folio, list] = await Promise.all([previewFolio(), listFraps()]);
      setNextFolio(folio);
      setFraps(list);
    } catch {
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogout = () => { logout(); navigate('/'); };

  const handleDelete = async (id, folio) => {
    if (!window.confirm(`¿Eliminar FRAP #${folio}? Esta acción no se puede deshacer.`)) return;
    setDeleting(id);
    try {
      await deleteFrap(id);
      setFraps(p => p.filter(f => f.id !== id));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const handleServicioChange = async (id, servicio) => {
    try {
      await updateFrapServicio(id, servicio);
      setFraps(p => p.map(f => f.id === id ? { ...f, servicio } : f));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error categorizando servicio');
    }
  };

  const handlePdf = async (frap) => {
    try {
      // Fetch full FRAP data including form_data
      const fullFrap = await getFrap(frap.id);
      const formData = fullFrap.form_data || fullFrap;
      const bytes = await generateFrapPdf(formData);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FRAP-${frap.folio}-${frap.nombre_paciente || 'paciente'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      setError('Error generando PDF');
    }
  };

  const filteredFraps = fraps.filter(f => {
    const matchFolio = f.folio?.toString().toLowerCase().includes(searchFolio.toLowerCase());
    const matchUser = f.created_by_name?.toLowerCase().includes(searchUser.toLowerCase());
    return matchFolio && matchUser;
  });

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="bg-[#1a3a5c] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-9 w-9 rounded-full object-cover" />
            <div>
              <p className="text-white font-black text-sm leading-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>FRAP Digital</p>
              <p className="text-slate-400 text-xs leading-tight">Cuerpo de Rescate Ensenada</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1">
              {user?.role === 'admin' ? <Shield className="w-3 h-3 text-amber-400" /> : <User className="w-3 h-3 text-green-400" />}
              <span className="text-white text-xs font-medium">{user?.name}</span>
              {user?.ambulance && <span className="text-[#4ade80] text-xs font-bold">· Unidad {user.ambulance}</span>}
            </div>
            <button onClick={handleLogout} className="flex items-center gap-1.5 text-slate-300 hover:text-white text-xs font-medium transition-colors px-2 py-1" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Top row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-black text-[#1a3a5c]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {user?.role === 'admin' ? 'Panel de Administrador' : `Bienvenido, ${user?.name}`}
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {user?.role === 'admin'
                ? `Viendo todos los FRAPs del sistema · Próximo folio: #${nextFolio}`
                : `Unidad ${user?.ambulance} · Próximo folio: #${nextFolio}`}
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/estadisticas')}
                className="flex items-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-black text-sm px-5 py-2.5 rounded-md transition-all shadow-sm active:scale-95 uppercase tracking-wide border border-slate-300"
              >
                Estadísticas
              </button>
            )}
            <button
              onClick={() => navigate('/frap/nuevo')}
              className="flex items-center gap-2 bg-[#1a7a4a] hover:bg-[#145d38] text-white font-black text-sm px-5 py-2.5 rounded-md transition-all shadow-md active:scale-95 uppercase tracking-wide"
              data-testid="create-frap-btn"
            >
              <FilePlus className="w-4 h-4" /> Nuevo FRAP
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4" data-testid="dashboard-error">
            <AlertCircle className="w-4 h-4" />{error}
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">×</button>
          </div>
        )}

        {/* FRAP list */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-[#1a3a5c] px-4 py-3 flex items-center justify-between">
            <h2 className="text-white font-black text-sm uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {user?.role === 'admin' ? 'Todos los FRAPs' : 'Mis FRAPs'}
            </h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Folio..."
                  value={searchFolio}
                  onChange={e => setSearchFolio(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded px-2 py-1 text-xs text-white placeholder:text-slate-400 focus:outline-none focus:border-green-400 w-24 transition-all"
                />
              </div>
              {user?.role === 'admin' && (
                <div className="relative">
                  <select
                    value={searchUser}
                    onChange={e => setSearchUser(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-green-400 w-32 transition-all cursor-pointer appearance-none"
                  >
                    <option value="" className="text-slate-900">Todos Usuarios</option>
                    {[...new Set(fraps.map(f => f.created_by_name).filter(Boolean))].sort().map(name => (
                      <option key={name} value={name} className="text-slate-900">{name}</option>
                    ))}
                  </select>
                </div>
              )}
              <span className="text-slate-400 text-xs ml-2">{filteredFraps.length} de {fraps.length}</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-[#1a7a4a] rounded-full animate-spin" />
              Cargando FRAPs...
            </div>
          ) : fraps.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <FilePlus className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm font-medium">No hay FRAPs registrados aún</p>
              <button onClick={() => navigate('/frap/nuevo')} className="mt-4 text-[#1a7a4a] text-sm font-bold hover:underline">
                + Crear el primer FRAP
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fraps-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Folio</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Fecha</th>
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Paciente</th>
                    {user?.role === 'admin' && (
                      <>
                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Creado por</th>
                        <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Categoría de Servicio</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Prioridad</th>
                    <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredFraps.map(frap => (
                    <tr key={frap.id} className="hover:bg-slate-50 transition-colors" data-testid={`frap-row-${frap.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-black text-[#1a3a5c] text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          #{frap.folio}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {frap.fecha || '—'}
                        <div className="text-slate-400 text-[10px]">{new Date(frap.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">{frap.nombre_paciente || <span className="text-slate-400 italic">Sin nombre</span>}</span>
                      </td>
                      {user?.role === 'admin' && (
                        <>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <Ambulance className="w-3 h-3" />
                              {frap.created_by_name}
                              {frap.unit && <span className="text-[#1a7a4a] font-bold">· U{frap.unit}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={frap.servicio || ''}
                              onChange={(e) => handleServicioChange(frap.id, e.target.value)}
                              className="text-[10px] border border-slate-300 rounded px-1.5 py-1 bg-white text-slate-700 focus:outline-none focus:border-[#1a7a4a] w-36 overflow-hidden text-ellipsis shadow-sm hover:border-slate-400 transition-colors cursor-pointer"
                            >
                              <option value="">Sin Categoría</option>
                              {[
                                'Embarazos con traslado', 'Embarazos sin traslados', 'Atendidos en base con traslado', 'Atendidos en base sin traslado',
                                'Atropellados sin traslado', 'Atropellados con traslado', 'Choques con lesionados con traslado', 'Choques con lesionados sin traslado',
                                'Desbarrancados con traslado', 'Desbarrancados sin traslado', 'Enfermos trasladados', 'Enfermos estabilizados en su domicilio',
                                'Pacientes estado etilico / otros', 'Lesionados (Riñas, caidas, otros) con traslado', 'Lesionados (Riñas, caidas, otros) sin traslado',
                                'Prensados con traslado', 'Prensados sin traslado', 'Intentos de suicidios', 'Suicidios concretados',
                                'Traslados locales', 'Traslados foraneos', 'Violaciones', 'Volcaduras con lesionados con traslado', 'Volcaduras con lesionados sin traslado',
                                'Busquedas', 'Fallecidos', 'Eventos locales', 'Eventos foraneos', 'Servicios cancelados en el trayecto'
                              ].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3">
                        {frap.prioridad ? (
                          <span className={`inline-block border text-[10px] font-black uppercase px-2 py-0.5 rounded-sm ${PRIORITY_STYLE[frap.prioridad] || 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                            {frap.prioridad}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {user?.role === 'admin' ? (
                            <button
                              onClick={() => navigate(`/frap/editar/${frap.id}`)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                              data-testid={`edit-frap-${frap.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" /> Editar
                            </button>
                          ) : (
                            <button
                              onClick={() => navigate(`/frap/ver/${frap.id}`)}
                              className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 font-medium px-2 py-1 hover:bg-slate-50 rounded transition-colors"
                              data-testid={`view-frap-${frap.id}`}
                            >
                              <Eye className="w-3.5 h-3.5" /> Ver
                            </button>
                          )}
                          <button
                            onClick={() => handlePdf(frap)}
                            className="flex items-center gap-1 text-xs text-[#1a7a4a] hover:text-[#145d38] font-medium px-2 py-1 hover:bg-green-50 rounded transition-colors"
                            data-testid={`pdf-frap-${frap.id}`}
                          >
                            <FileDown className="w-3.5 h-3.5" /> PDF
                          </button>
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => handleDelete(frap.id, frap.folio)}
                              disabled={deleting === frap.id}
                              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              data-testid={`delete-frap-${frap.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {deleting === frap.id ? '...' : 'Eliminar'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
